import React, { useState, useEffect } from 'react';
import { Save, Truck, AlertCircle, Loader2 } from 'lucide-react';
import { getShippingConfig, saveShippingConfig } from '../api/shippingApi';

const AdminShipping = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({
        cashPrice: 0,
        cardPrice: 0,
        minimumPoints: 0
    });
    const [message, setMessage] = useState(null);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            const data = await getShippingConfig();
            setConfig(prev => ({
                ...prev,
                ...data,
                // Garantizamos que existan los campos incluso si vienen de una versión anterior
                cashPrice: data.cashPrice ?? data.price ?? 0,
                cardPrice: data.cardPrice ?? data.price ?? 0
            }));
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al cargar la configuración' });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);

        try {
            await saveShippingConfig(config);
            await loadConfig(); // Reload to get the new ID if created
            setMessage({ type: 'success', text: 'Configuración guardada correctamente' });

            // Clear success message after 3 seconds
            setTimeout(() => {
                setMessage(null);
            }, 3000);
        } catch (error) {
            setMessage({ type: 'error', text: 'Error al guardar la configuración' });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: Number(value)
        }));
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-purple-100 rounded-xl text-purple-600">
                    <Truck className="w-8 h-8" />
                </div>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Configuración de Envío</h1>
                    <p className="text-gray-500">Gestiona los costos y reglas para el envío gratis</p>
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-lg mb-6 flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                    <AlertCircle className="w-5 h-5" />
                    <span>{message.text}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Costo de Envío (Efectivo)
                            </label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                <input
                                    type="number"
                                    name="cashPrice"
                                    min="0"
                                    required
                                    className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                    value={config.cashPrice}
                                    onChange={handleChange}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Costo de Envío (Tarjeta)
                            </label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                <input
                                    type="number"
                                    name="cardPrice"
                                    min="0"
                                    required
                                    className="w-full pl-8 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                    value={config.cardPrice}
                                    onChange={handleChange}
                                    placeholder="0.00"
                                />
                            </div>
                        </div>
                    </div>
                    <p className="text-sm text-gray-500">
                        Estos son los costos base que se aplicarán según el medio de pago si no se cumple la condición de puntos.
                    </p>

                    <div className="space-y-2 pt-4">
                        <label className="block text-sm font-medium text-gray-700">
                            Puntaje Mínimo para Envío Gratis
                        </label>
                        <input
                            type="number"
                            name="minimumPoints"
                            min="0"
                            required
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                            value={config.minimumPoints}
                            onChange={handleChange}
                            placeholder="0"
                        />
                        <p className="text-sm text-gray-500">
                            Si la suma de puntos de los productos supera este valor, el envío será gratuito para cualquier medio de pago.
                        </p>
                    </div>
                </div>

                <div className="pt-4 border-t border-gray-100 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-3 rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Guardar Cambios
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminShipping;
